// 실시간 1:1 피드백 슬롯 예약 API
// - instructorAvailability/{proUid}: 프로가 설정한 주간 가능 시간
// - realtimeBookings/{slotKey}: 예약된 슬롯 (문서 ID 유니크로 중복 예약 방지)

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import { getFirebaseDb } from "@/lib/api/firebase-ultra-safe";

export interface WeeklyRule {
  /** 0(일) ~ 6(토) */
  day: number;
  /** "20:00" */
  start: string;
  /** "22:00" */
  end: string;
}

export interface InstructorAvailability {
  instructorId: string;
  instructorName?: string;
  slotMinutes: number;
  weeklyRules: WeeklyRule[];
  /** 휴무 날짜 목록 ("2026-07-20") */
  blockedDates: string[];
  updatedAt?: unknown;
}

export interface RealtimeBooking {
  instructorId: string;
  instructorName?: string;
  studentId: string;
  studentName?: string;
  /** "2026-07-16" */
  date: string;
  /** "14:30" */
  time: string;
  status: "reserved" | "completed";
  feedbackRequestId?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface SlotOption {
  date: string;
  time: string;
  instructorId: string;
  instructorName?: string;
}

export const DEFAULT_SLOT_MINUTES = 30;
export const BOOKING_DAYS_AHEAD = 30;
/** 당일 예약 시 최소 리드타임 (밀리초) */
const MIN_LEAD_TIME_MS = 2 * 60 * 60 * 1000;

export function slotKey(instructorId: string, date: string, time: string) {
  return `${instructorId}_${date}_${time.replace(":", "")}`;
}

export function formatDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function timeToMinutes(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
}

function minutesToTime(min: number): string {
  const h = String(Math.floor(min / 60)).padStart(2, "0");
  const m = String(min % 60).padStart(2, "0");
  return `${h}:${m}`;
}

export async function getAvailability(
  instructorId: string
): Promise<InstructorAvailability | null> {
  const db = await getFirebaseDb();
  if (!db) return null;
  const snap = await getDoc(doc(db, "instructorAvailability", instructorId));
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    instructorId,
    instructorName: data.instructorName,
    slotMinutes: data.slotMinutes || DEFAULT_SLOT_MINUTES,
    weeklyRules: Array.isArray(data.weeklyRules) ? data.weeklyRules : [],
    blockedDates: Array.isArray(data.blockedDates) ? data.blockedDates : [],
  };
}

export async function saveAvailability(
  availability: InstructorAvailability
): Promise<void> {
  const db = await getFirebaseDb();
  if (!db) throw new Error("데이터베이스 연결에 실패했습니다.");
  await setDoc(
    doc(db, "instructorAvailability", availability.instructorId),
    {
      instructorId: availability.instructorId,
      instructorName: availability.instructorName || "",
      slotMinutes: availability.slotMinutes || DEFAULT_SLOT_MINUTES,
      weeklyRules: availability.weeklyRules,
      blockedDates: availability.blockedDates,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/** 실시간 피드백 가능 시간을 설정한 모든 프로 조회 */
export async function listAvailabilities(): Promise<InstructorAvailability[]> {
  const db = await getFirebaseDb();
  if (!db) return [];
  const snap = await getDocs(collection(db, "instructorAvailability"));
  return snap.docs
    .map(d => {
      const data = d.data();
      return {
        instructorId: d.id,
        instructorName: data.instructorName,
        slotMinutes: data.slotMinutes || DEFAULT_SLOT_MINUTES,
        weeklyRules: Array.isArray(data.weeklyRules) ? data.weeklyRules : [],
        blockedDates: Array.isArray(data.blockedDates) ? data.blockedDates : [],
      } as InstructorAvailability;
    })
    .filter(a => a.weeklyRules.length > 0);
}

/** 특정 프로들의 예약된 슬롯 조회 (오늘 이후) */
export async function listUpcomingBookings(
  instructorIds: string[]
): Promise<Set<string>> {
  const db = await getFirebaseDb();
  const taken = new Set<string>();
  if (!db || instructorIds.length === 0) return taken;

  const today = formatDateKey(new Date());
  // Firestore in 쿼리는 10개 제한이므로 나눠서 조회
  for (let i = 0; i < instructorIds.length; i += 10) {
    const chunk = instructorIds.slice(i, i + 10);
    const q = query(
      collection(db, "realtimeBookings"),
      where("instructorId", "in", chunk),
      where("date", ">=", today)
    );
    const snap = await getDocs(q);
    snap.docs.forEach(d => taken.add(d.id));
  }
  return taken;
}

/**
 * 예약 가능한 슬롯 생성 (여러 프로의 가용 시간 병합).
 * 같은 시간에 여러 프로가 가능하면 먼저 조회된 프로에게 배정합니다.
 */
export function buildSlotsByDate(
  availabilities: InstructorAvailability[],
  takenSlotKeys: Set<string>,
  daysAhead: number = BOOKING_DAYS_AHEAD
): Map<string, SlotOption[]> {
  const slotsByDate = new Map<string, SlotOption[]>();
  const now = new Date();
  const minStartMs = now.getTime() + MIN_LEAD_TIME_MS;

  for (let offset = 0; offset <= daysAhead; offset++) {
    const d = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate() + offset
    );
    const dateKey = formatDateKey(d);
    const dayOfWeek = d.getDay();
    const seenTimes = new Set<string>();
    const options: SlotOption[] = [];

    for (const avail of availabilities) {
      if (avail.blockedDates.includes(dateKey)) continue;
      const step = avail.slotMinutes || DEFAULT_SLOT_MINUTES;

      for (const rule of avail.weeklyRules) {
        if (rule.day !== dayOfWeek) continue;
        const startMin = timeToMinutes(rule.start);
        const endMin = timeToMinutes(rule.end);

        for (let min = startMin; min + step <= endMin; min += step) {
          const time = minutesToTime(min);
          if (seenTimes.has(time)) continue;
          if (takenSlotKeys.has(slotKey(avail.instructorId, dateKey, time))) {
            continue;
          }
          const slotStart = new Date(
            d.getFullYear(),
            d.getMonth(),
            d.getDate(),
            Math.floor(min / 60),
            min % 60
          );
          if (slotStart.getTime() < minStartMs) continue;

          seenTimes.add(time);
          options.push({
            date: dateKey,
            time,
            instructorId: avail.instructorId,
            instructorName: avail.instructorName,
          });
        }
      }
    }

    if (options.length > 0) {
      options.sort((a, b) => a.time.localeCompare(b.time));
      slotsByDate.set(dateKey, options);
    }
  }

  return slotsByDate;
}

/**
 * 슬롯 예약. 문서 ID가 슬롯 키라서 이미 예약된 슬롯이면 실패합니다.
 * @returns 예약 문서 ID (슬롯 키)
 */
export async function reserveSlot(params: {
  slot: SlotOption;
  studentId: string;
  studentName?: string;
  feedbackRequestId?: string;
}): Promise<string> {
  const db = await getFirebaseDb();
  if (!db) throw new Error("데이터베이스 연결에 실패했습니다.");

  const key = slotKey(
    params.slot.instructorId,
    params.slot.date,
    params.slot.time
  );
  const ref = doc(db, "realtimeBookings", key);

  const existing = await getDoc(ref);
  if (existing.exists()) {
    throw new Error(
      "방금 다른 학생이 예약한 시간입니다. 다른 시간을 선택해주세요."
    );
  }

  const booking: RealtimeBooking = {
    instructorId: params.slot.instructorId,
    instructorName: params.slot.instructorName || "",
    studentId: params.studentId,
    studentName: params.studentName || "",
    date: params.slot.date,
    time: params.slot.time,
    status: "reserved",
    ...(params.feedbackRequestId
      ? { feedbackRequestId: params.feedbackRequestId }
      : {}),
  };

  try {
    await setDoc(ref, {
      ...booking,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    // 규칙상 기존 문서 덮어쓰기(update)는 학생에게 허용되지 않으므로
    // 동시 예약 경합 시 여기로 떨어집니다.
    console.error("슬롯 예약 실패:", error);
    throw new Error(
      "방금 다른 학생이 예약한 시간입니다. 다른 시간을 선택해주세요."
    );
  }
  return key;
}

/** 예약 해제 (결제 취소·실패 시) */
export async function releaseSlot(bookingId: string): Promise<void> {
  const db = await getFirebaseDb();
  if (!db) return;
  try {
    await deleteDoc(doc(db, "realtimeBookings", bookingId));
  } catch (error) {
    console.warn("슬롯 해제 실패:", error);
  }
}
